import {
  LOGIN_PATH,
  buildLoginLocation,
  buildReturnUrl,
} from 'utils/return-url';
import React, { useEffect } from 'react';
import { Redirect, Route } from 'react-router-dom';
import {
  isAuthLoggedIn,
  isAuthSessionChecked,
  isAuthUserInitiatedLogout,
} from 'modules/auth/selectors';
import { useDispatch, useSelector } from 'react-redux';

import ExternalRedirect from 'views/components/external-redirect';
/* eslint-disable react/prop-types */
/* eslint-disable react/jsx-props-no-spreading */
import ScreenLoading from 'views/ui/screen-loading';
import { resolveAccountsLoginUrl } from 'utils/accounts';
import { verifySession } from 'modules/auth/actions';

/**
 * Gate for the app's private URLs. Someone without a session is handed to the
 * shared accounts app, which owns sign-in for the whole suite, carrying the URL
 * they asked for as `return` so signing in lands them there.
 *
 * The session cookie is checked with the API first. A cold page load has no auth
 * state even when the cookie is still good — accounts may have just signed the
 * user in and sent them here — and handing that user straight back would bounce
 * them between the two apps.
 */
const PrivateRoute = ({ render, ...rest }) => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector(isAuthLoggedIn);
  const sessionChecked = useSelector(isAuthSessionChecked);
  const userInitiatedLogout = useSelector(isAuthUserInitiatedLogout);

  // Logging out on purpose needs no check: the session was just given up.
  const mustCheckSession =
    !isLoggedIn && !sessionChecked && !userInitiatedLogout;

  useEffect(() => {
    if (mustCheckSession) {
      dispatch(verifySession());
    }
  }, [dispatch, mustCheckSession]);

  const signInRedirect = (location) => {
    // A deliberate log out lands on this app's own login page, with no `return`
    // back to the page being left.
    if (userInitiatedLogout) {
      return (
        <Redirect to={{ pathname: LOGIN_PATH, state: { from: location } }} />
      );
    }

    const accountsUrl = resolveAccountsLoginUrl(buildReturnUrl(location));

    // Nothing usable is configured for accounts. Fall back to the in-app login
    // page rather than navigating nowhere; it still carries the `return`.
    if (!accountsUrl) {
      return (
        <Redirect
          to={{ ...buildLoginLocation(location), state: { from: location } }}
        />
      );
    }

    return <ExternalRedirect url={accountsUrl} />;
  };

  return (
    <Route
      {...rest}
      render={(otherProps) => {
        if (isLoggedIn === true) {
          return render(otherProps);
        }
        if (mustCheckSession) {
          return <ScreenLoading />;
        }

        return signInRedirect(otherProps.location);
      }}
    />
  );
};

export default PrivateRoute;
